import { useState, useEffect, useMemo } from 'react'
import { supabase, SUPABASE_ANON_KEY } from '../supabaseClient'
import { C, st } from '../theme'
import { PageHeader, EmptyState, Modal } from './UI'

const ALL_MODULES = [
  { key: 'wms',         label: '倉儲管理',       en: 'Warehouse',   icon: '📦' },
  { key: 'production',  label: '生產管理',        en: 'Production',  icon: '🏭' },
  { key: 'shipping',    label: '出貨管理',        en: 'Shipping',    icon: '🚚' },
  { key: 'crm',         label: '客戶管理',        en: 'CRM',         icon: '👥' },
  { key: 'reports',     label: '報表分析',        en: 'Reports',     icon: '📊' },
  { key: 'maintenance', label: '機械維修',        en: 'Maintenance', icon: '🔧' },
  { key: 'fives',       label: '5S 管理',         en: '5S',          icon: '🏆' },
  { key: 'tasks',       label: '任務管理',        en: 'Tasks',       icon: '📋' },
  { key: 'purchasing',  label: '採購管理',        en: 'Purchasing',  icon: '🛒' },
  { key: 'attendance',  label: '出勤管理',        en: 'Attendance',  icon: '⏰' },
  { key: 'products',    label: '商品管理（進階）', en: 'Products',    icon: '🗂️' },
]
const SPECIAL_PERMS = [
  { key: 'delete_records',  label: '刪除紀錄',     icon: '🗑' },
  { key: 'delete_products', label: '刪除商品',     icon: '❌' },
  { key: 'manage_users',    label: '管理員工',     icon: '👑' },
  { key: 'assign',          label: '派發權限',     icon: '📤' },
  { key: 'approve_users',   label: '審核帳號',     icon: '✅' },
  { key: 'view_costs',      label: '查看成本',     icon: '💰' },
  { key: 'backup_export',   label: '全局備份匯出', icon: '🗄️' },
]
const ALL_PERM_KEYS = [...ALL_MODULES.map(m => m.key), ...SPECIAL_PERMS.map(s => s.key)]

const DEPARTMENTS = ['管理部','生產部','倉儲部','業務部','財務部','維修部','品管部','採購部','其他']

export default function EmployeeManager({ session, userProfile, showToast, addLog, isAdmin = false, companyModules = [] }) {
  // 只顯示超管已授權給本公司的模組（空陣列退回全部，避免超管介面空白）
  const availableModules = companyModules.length > 0
    ? ALL_MODULES.filter(m => companyModules.includes(m.key))
    : ALL_MODULES
  const [employees, setEmployees] = useState([])
  const [positions, setPositions] = useState([])
  const [modMap, setModMap] = useState({})   // { userId: { module: enabled } }
  const [saving, setSaving] = useState(false)
  const [quickApplyPos, setQuickApplyPos] = useState({}) // { empId: positionId } 套用選單暫存

  // Filters
  const [search, setSearch] = useState('')
  const [filterDept, setFilterDept] = useState('all')
  const [filterPos, setFilterPos] = useState('all')

  // Add/Edit modal
  const [showModal, setShowModal] = useState(false)
  const [editingUser, setEditingUser] = useState(null)
  const [form, setForm] = useState({ name:'', employee_id:'', email:'', password:'', department:'', phone:'', position_id:'' })

  // Manager modal
  const [showManagerModal, setShowManagerModal] = useState(false)
  const [editingManager, setEditingManager] = useState(null)
  const [managerForm, setManagerForm] = useState({ name:'', email:'', password:'', title:'', department:'', phone:'', position_id:'' })
  const [savingManager, setSavingManager] = useState(false)

  // Permission expand
  const [expandedPerms, setExpandedPerms] = useState(null) // userId

  const load = async () => {
    const cid = userProfile?.company_id
    let empQ = supabase.from('user_profiles').select('*').order('name')
    let posQ = supabase.from('company_positions').select('*').order('sort_order')
    if (cid) { empQ = empQ.eq('company_id', cid); posQ = posQ.eq('company_id', cid) }
    const [{ data: emps }, { data: pos }, { data: mods }] = await Promise.all([
      empQ,
      posQ,
      supabase.from('module_permissions').select('user_id,module,enabled'),
    ])
    if (emps) setEmployees(emps.filter(e => !e.is_super_admin))
    if (pos)  setPositions(pos)
    if (mods) {
      const map = {}
      mods.forEach(m => {
        if (!map[m.user_id]) map[m.user_id] = {}
        map[m.user_id][m.module] = m.enabled
      })
      setModMap(map)
    }
  }

  useEffect(() => { load() }, [])

  // ── Derived ──
  const depts = useMemo(() => {
    const s = new Set(employees.map(e => e.department).filter(Boolean))
    return [...s].sort()
  }, [employees])

  const filtered = useMemo(() => employees.filter(e => {
    if (search && !e.name?.toLowerCase().includes(search.toLowerCase()) &&
        !e.employee_id?.toLowerCase().includes(search.toLowerCase()) &&
        !e.email?.toLowerCase().includes(search.toLowerCase())) return false
    if (filterDept !== 'all' && e.department !== filterDept) return false
    if (filterPos !== 'all' && e.position_id !== filterPos) return false
    return true
  }), [employees, search, filterDept, filterPos])

  // ── Handlers ──
  const openAdd = () => {
    setEditingUser(null)
    setForm({ name:'', employee_id:'', password:'', department:'', phone:'', position_id:'' })
    setShowModal(true)
  }

  const openEdit = (emp) => {
    setEditingUser(emp)
    setForm({ name: emp.name||'', employee_id: emp.employee_id||'', password:'', department: emp.department||'', phone: emp.phone||'', position_id: emp.position_id||'' })
    setShowModal(true)
  }

  const openEditManager = (emp) => {
    // title 欄位若被誤存為 email（Supabase trigger 預設行為），則清空
    const safeTitle = (emp.title && !emp.title.includes('@')) ? emp.title : ''
    setManagerForm({ name: emp.name||'', email: emp.email||'', password:'', title: safeTitle, department: emp.department||'', phone: emp.phone||'', position_id: emp.position_id||'' })
    setEditingManager(emp)
    setShowManagerModal(true)
  }

  const handleSave = async () => {
    if (!form.name.trim()) { showToast('請填寫員工姓名', 'error'); return }
    if (!form.employee_id.trim()) { showToast('請填寫工號', 'error'); return }
    if (!editingUser) {
      if (!form.password) { showToast('請設定密碼', 'error'); return }
      if (!/^\d+$/.test(form.password)) { showToast('密碼只能是數字', 'error'); return }
      if (form.password.length < 6) { showToast('密碼至少 6 位數字', 'error'); return }
    }
    setSaving(true)
    try {
      const posObj = positions.find(p => p.id === form.position_id)
      if (editingUser) {
        const newEmpId = form.employee_id.trim().toLowerCase()
        const oldEmpId = (editingUser.employee_id || '').toLowerCase()
        const empIdChanged = newEmpId !== oldEmpId
        const passwordChanged = form.password && form.password.length >= 6

        // 若工號或密碼有變更 → 呼叫 update-user Edge Function
        if (empIdChanged || passwordChanged) {
          const { data: { session: cur } } = await supabase.auth.getSession()
          // 從舊 email 抓出 short_code（格式：工號@short_code.local）
          const oldEmail = editingUser.email || ''
          const shortCode = oldEmail.includes('@') ? oldEmail.split('@')[1].replace('.local', '') : 'cyerp'
          const updatePayload = { accessToken: cur.access_token, userId: editingUser.id }
          if (empIdChanged) updatePayload.newEmail = `${newEmpId}@${shortCode}.local`
          if (passwordChanged) updatePayload.newPassword = form.password
          const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/update-user`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${cur.access_token}` },
            body: JSON.stringify(updatePayload),
          })
          const result = await res.json()
          if (result.error) { showToast('更新登入資訊失敗：' + result.error, 'error'); setSaving(false); return }
        }

        // 更新 user_profiles（email 同步由 Edge Function 處理，這裡只存工號字串）
        await supabase.from('user_profiles').update({
          name: form.name.trim(),
          employee_id: form.employee_id.trim(),
          department: form.department.trim(),
          phone: form.phone.trim(),
          position_id: form.position_id || null,
          position: posObj?.name || '',
        }).eq('id', editingUser.id)

        // 職位有變更時自動套用新職位權限
        if (form.position_id && form.position_id !== editingUser.position_id) {
          await applyPositionPerms(editingUser.id, form.position_id)
        }

        const changes = []
        if (empIdChanged) changes.push(`工號 ${oldEmpId.toUpperCase()} → ${newEmpId.toUpperCase()}`)
        if (passwordChanged) changes.push('密碼已重設')
        await addLog('更新員工', `更新員工資料：${form.name}${changes.length ? `（${changes.join('、')}）` : ''}`)
        showToast(empIdChanged ? `✓ 已更新，新登入工號：${form.employee_id.trim().toUpperCase()}` : '✓ 員工資料已更新')
      } else {
        // Email 由 Edge Function 依公司 short_code 自動產生：工號@{short_code}.local
        const { data: { session: cur } } = await supabase.auth.getSession()
        const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/create-user`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${cur.access_token}` },
          body: JSON.stringify({ accessToken: cur.access_token, employees: [{
            name: form.name.trim(),
            employee_id: form.employee_id.trim(),
            email: '',
            password: form.password,
            phone: form.phone.trim(),
            department: form.department.trim(),
            position: posObj?.name || '',
            position_id: form.position_id || null,
            company_id: userProfile?.company_id || null,
          }] }),
        })
        const data = await res.json()
        // 頂層錯誤（403 / 500）
        if (data.error) { showToast('建立失敗：' + data.error, 'error'); setSaving(false); return }
        const result = (data.results || [])[0]
        if (!result?.success) { showToast('建立失敗：' + (result?.error || JSON.stringify(data)), 'error'); setSaving(false); return }
        // 自動套用職位預設權限
        if (form.position_id && result.userId) {
          await applyPositionPerms(result.userId, form.position_id)
        }
        await addLog('新增員工', `新增員工：${form.name}（工號：${form.employee_id}）`)
        showToast(`✓ 員工「${form.name}」帳號已建立`)
      }
      setShowModal(false); load()
    } catch (e) { showToast('操作失敗：' + e.message, 'error') }
    setSaving(false)
  }

  const handleDelete = async (id, uname) => {
    if (!window.confirm(`確定刪除員工「${uname}」的帳號？此操作無法復原。`)) return
    try {
      // Step 1: Delete related data directly via supabase client
      await supabase.from('module_permissions').delete().eq('user_id', id)
      await supabase.from('attendance').delete().eq('user_id', id)
      await supabase.from('leave_requests').delete().eq('user_id', id)

      // Step 2: Delete user_profiles directly (requires DELETE RLS policy)
      const { data: deleted, error: profileErr } = await supabase
        .from('user_profiles').delete().eq('id', id).select('id')
      if (profileErr) { showToast('刪除失敗：' + profileErr.message + '\n請在 Supabase SQL Editor 執行：CREATE POLICY "管理員可刪除員工" ON user_profiles FOR DELETE TO authenticated USING (true);', 'error'); return }
      if (!deleted || deleted.length === 0) {
        showToast('刪除失敗：權限不足，請在 Supabase SQL Editor 執行：\nCREATE POLICY "管理員可刪除員工" ON user_profiles FOR DELETE TO authenticated USING (true);', 'error'); return
      }

      // Step 3: Also remove auth user via Edge Function (best-effort, UI already updated)
      try {
        const { data: { session: cur } } = await supabase.auth.getSession()
        await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/delete-user`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${cur.access_token}` },
          body: JSON.stringify({ userId: id }),
        })
      } catch (_) { /* auth record cleanup is best-effort */ }

      await addLog('刪除員工', `刪除員工：${uname}`)
      showToast('✓ 已刪除'); load()
    } catch (e) {
      showToast('刪除失敗：' + e.message, 'error')
    }
  }

  const handleToggleAdmin = async (emp) => {
    const newRole = emp.role === 'admin' ? 'user' : 'admin'
    await supabase.from('user_profiles').update({ role: newRole }).eq('id', emp.id)
    await addLog('變更角色', `${emp.name} → ${newRole}`)
    showToast(`✓ 已${newRole === 'admin' ? '設為管理員' : '取消管理員'}`)
    load()
  }

  const openAddManager = () => {
    setEditingManager(null)
    setManagerForm({ name:'', email:'', password:'', title:'', department:'', phone:'', position_id:'' })
    setShowManagerModal(true)
  }

  const handleSaveManager = async () => {
    if (!managerForm.name.trim()) { showToast('請填寫姓名', 'error'); return }
    setSavingManager(true)
    try {
      const posObj = positions.find(p => p.id === managerForm.position_id)
      if (editingManager) {
        // 編輯主管
        await supabase.from('user_profiles').update({
          name: managerForm.name.trim(),
          title: managerForm.title.trim(),
          department: managerForm.department,
          phone: managerForm.phone.trim(),
          position_id: managerForm.position_id || null,
          position: posObj?.name || '',
          updated_at: new Date().toISOString(),
        }).eq('id', editingManager.id)
        // 職位有變更時套用新職位權限（主管不改變 role）
        if (managerForm.position_id && managerForm.position_id !== editingManager.position_id) {
          await applyPositionPerms(editingManager.id, managerForm.position_id, false)
        }
        if (managerForm.password && managerForm.password.length >= 6) {
          const { data: { session: cur } } = await supabase.auth.getSession()
          await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/update-password`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${cur.access_token}` },
            body: JSON.stringify({ userId: editingManager.id, password: managerForm.password }),
          }).catch(() => {})
        }
        await addLog('更新主管', `更新主管資料：${managerForm.name}`)
        showToast(`✓ 主管「${managerForm.name}」資料已更新`)
      } else {
        // 新增主管
        if (!managerForm.email.trim() || !managerForm.email.includes('@')) { showToast('請填寫有效的 Email', 'error'); setSavingManager(false); return }
        if (!managerForm.password || managerForm.password.length < 6) { showToast('密碼至少 6 位', 'error'); setSavingManager(false); return }
        const { data: { session: cur } } = await supabase.auth.getSession()
        const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/create-user`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${cur.access_token}` },
          body: JSON.stringify({ accessToken: cur.access_token, employees: [{
            name: managerForm.name.trim(),
            employee_id: managerForm.email.split('@')[0],
            email: managerForm.email.trim().toLowerCase(),
            password: managerForm.password,
            title: managerForm.title.trim(),
            department: managerForm.department,
            phone: managerForm.phone.trim(),
            position_id: managerForm.position_id || null,
            position: posObj?.name || '',
            company_id: userProfile?.company_id || null,
            role: 'manager',
          }] }),
        })
        const data = await res.json()
        if (data.error) { showToast('建立失敗：' + data.error, 'error'); setSavingManager(false); return }
        const result = data.results?.[0]
        if (!result?.success) { showToast('建立失敗：' + (result?.error || JSON.stringify(data)), 'error'); setSavingManager(false); return }
        // 套用職位預設權限（主管不改變 role）
        if (managerForm.position_id && result.userId) {
          await applyPositionPerms(result.userId, managerForm.position_id, false)
        }
        await addLog('新增主管', `新增主管帳號：${managerForm.name}（${managerForm.email}）`)
        showToast(`✓ 主管「${managerForm.name}」帳號已建立`)
      }
      setShowManagerModal(false); load()
    } catch (e) { showToast('操作失敗：' + e.message, 'error') }
    setSavingManager(false)
  }

  const applyPositionPerms = async (empId, posId, updateRole = true) => {
    const pos = positions.find(p => p.id === posId)
    if (!pos) return
    const perms = pos.permissions || {}
    const inserts = ALL_PERM_KEYS.map(k => ({ user_id: empId, module: k, enabled: !!perms[k], company_id: userProfile?.company_id || null }))
    await supabase.from('module_permissions').upsert(inserts, { onConflict: 'user_id,module' })
    if (updateRole) {
      const newRole = pos.is_admin ? 'admin' : 'user'
      await supabase.from('user_profiles').update({ role: newRole }).eq('id', empId)
    }
  }

  const handleApplyPositionDefaults = async (empId, posId) => {
    const pos = positions.find(p => p.id === posId)
    if (!pos) { showToast('請先指派職位','error'); return }
    await applyPositionPerms(empId, posId)
    await addLog('套用職位預設', `套用「${pos.name}」權限 → ${(employees.find(e=>e.id===empId)||{}).name}`)
    showToast(`✓ 已套用「${pos.name}」預設權限`)
    load()
  }

  const handleToggleModule = async (empId, module, current) => {
    const existing = await supabase.from('module_permissions').select('id').eq('user_id', empId).eq('module', module).maybeSingle()
    if (existing.data) await supabase.from('module_permissions').update({ enabled: !current }).eq('user_id', empId).eq('module', module)
    else await supabase.from('module_permissions').insert({ user_id: empId, module, enabled: !current, company_id: userProfile?.company_id || null })
    setModMap(prev => ({ ...prev, [empId]: { ...(prev[empId]||{}), [module]: !current } }))
  }

  const handleExportCSV = () => {
    const headers = ['姓名','工號','Email','部門','電話','職位','角色','權限模組數']
    const rows = employees.map(e => {
      const pos = positions.find(p => p.id === e.position_id)
      const mods = modMap[e.id] || {}
      const modCount = availableModules.filter(m => mods[m.key]).length
      return [e.name, e.employee_id||'', e.email||'', e.department||'', e.phone||'', pos?.name||'', e.role||'user', modCount]
    })
    const csv = [headers,...rows].map(r => r.map(v=>`"${v}"`).join(',')).join('\n')
    const blob = new Blob(['\uFEFF'+csv],{type:'text/csv;charset=utf-8;'})
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob)
    a.download = `員工名單_${new Date().toISOString().slice(0,10)}.csv`; a.click()
  }

  // Stats
  const adminCount = employees.filter(e => e.role==='admin').length
  const managerCount = employees.filter(e => e.role==='manager').length
  const deptBreakdown = useMemo(() => {
    const map = {}
    employees.forEach(e => { const d = e.department||'未設定'; map[d] = (map[d]||0)+1 })
    return Object.entries(map).sort((a,b)=>b[1]-a[1]).slice(0,5)
  }, [employees])

  return (
    <div>
      <PageHeader title="👥 員工管理" en="Employee Management" subtitle="帳號管理、職位指派、模組權限" />

      {/* Stats row */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))', gap:12, marginBottom:20 }}>
        {[
          { icon:'👥', label:'員工總數',   value: employees.length, color: C.accent,  bg: C.accentDim },
          { icon:'⚙️',  label:'管理員',     value: adminCount,        color:'#7c3aed', bg:'#ede9fe' },
          { icon:'👔', label:'主管',        value: managerCount,       color:'#0891b2', bg:'#e0f2fe' },
          { icon:'✅', label:'已核准帳號', value: employees.filter(e=>e.approved).length, color: C.green, bg: C.greenBg },
          { icon:'⏳', label:'待審核',     value: employees.filter(e=>e.approved===false).length, color: C.yellow, bg: C.yellowBg },
        ].map((s,i) => (
          <div key={i} style={{ background:s.bg, border:`2px solid ${s.color}33`, borderRadius:14, padding:'14px 16px' }}>
            <div style={{ fontSize:22, marginBottom:4 }}>{s.icon}</div>
            <div style={{ fontSize:26, fontWeight:900, color:s.color }}>{s.value}</div>
            <div style={{ fontSize:12, color:C.sub, fontWeight:600, marginTop:2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Dept breakdown */}
      {deptBreakdown.length > 0 && (
        <div style={{ ...st.card, marginBottom:16, padding:'14px 18px' }}>
          <div style={{ fontSize:13, fontWeight:700, color:C.text, marginBottom:10 }}>🏢 部門分布</div>
          <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
            {deptBreakdown.map(([dept, count]) => (
              <button key={dept} style={{ padding:'5px 14px', borderRadius:20, background: filterDept===dept?C.accentDim:'#f1f5f9', border:`1.5px solid ${filterDept===dept?C.accent:C.border}`, fontSize:12, fontWeight:600, color: filterDept===dept?C.accent:C.sub, cursor:'pointer' }}
                onClick={() => setFilterDept(filterDept===dept?'all':dept)}>
                {dept} <span style={{ fontWeight:800 }}>{count}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap', alignItems:'center' }}>
        <input style={{ ...st.input, flex:1, minWidth:200, maxWidth:280 }} value={search} placeholder="🔍 姓名 / 工號 / Email" onChange={e => setSearch(e.target.value)} />
        <select style={{ ...st.input, width:140 }} value={filterDept} onChange={e => setFilterDept(e.target.value)}>
          <option value="all">全部部門</option>
          {depts.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        <select style={{ ...st.input, width:150 }} value={filterPos} onChange={e => setFilterPos(e.target.value)}>
          <option value="all">全部職位</option>
          {positions.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        {(search||filterDept!=='all'||filterPos!=='all') && (
          <button style={{ ...st.btn('default'), padding:'9px 14px', fontSize:12 }} onClick={() => { setSearch(''); setFilterDept('all'); setFilterPos('all') }}>✕ 清除</button>
        )}
        <div style={{ marginLeft:'auto', display:'flex', gap:8 }}>
          <button style={{ ...st.btn('default'), padding:'10px 16px', fontSize:13 }} onClick={handleExportCSV}>📤 匯出 CSV</button>
          {isAdmin && (
            <button style={{ ...st.btn('default'), padding:'10px 16px', fontSize:13, background:'#e0f2fe', color:'#0891b2', border:'1.5px solid #bae6fd' }} onClick={openAddManager}>
              <span>👔 新增主管</span>
              <span style={{ fontSize:9, opacity:0.6, display:'block' }}>Add Manager</span>
            </button>
          )}
          <button style={{ ...st.btn(), padding:'10px 18px' }} onClick={openAdd}>
            <span>+ 新增員工</span>
            <span style={{ fontSize:9, opacity:0.5, display:'block' }}>Add Employee</span>
          </button>
        </div>
      </div>

      <div style={{ fontSize:12, color:C.muted, marginBottom:12 }}>共 {employees.length} 位帳號（主管 {managerCount} · 員工 {employees.length - managerCount}）</div>

      {employees.length === 0 && <div style={st.card}><EmptyState icon="👥" text="尚無帳號，點右上角新增" /></div>}

      {/* ── 主管區塊 ── */}
      {employees.filter(e => e.role === 'manager').length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12 }}>
            <div style={{ fontSize:14, fontWeight:800, color:'#0891b2' }}>👔 主管列表</div>
            <div style={{ flex:1, height:2, background:'linear-gradient(to right, #bae6fd, transparent)', borderRadius:2 }} />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))', gap:12 }}>
            {employees.filter(e => e.role === 'manager').map(emp => {
              const isSelf = emp.id === session?.user?.id
              const mods = modMap[emp.id] || {}
              const enabledMods = availableModules.filter(m => mods[m.key])
              const enabledSpec = SPECIAL_PERMS.filter(s => mods[s.key])
              const isExpanded = expandedPerms === emp.id
              const pos = positions.find(p => p.id === emp.position_id)
              return (
                <div key={emp.id} style={{ background:'#fff', borderRadius:14, border:'2px solid #bae6fd', overflow:'hidden', boxShadow:'0 2px 8px rgba(8,145,178,0.08)' }}>
                  {/* 主管資訊 header */}
                  <div style={{ background:'linear-gradient(135deg,#e0f2fe,#f0f9ff)', padding:'14px 16px', borderBottom:'1px solid #bae6fd', display:'flex', gap:12, alignItems:'center' }}>
                    <div style={{ width:46, height:46, borderRadius:'50%', background:'#0891b2', display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, fontWeight:800, color:'#fff', flexShrink:0 }}>
                      {(emp.name||'?')[0].toUpperCase()}
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
                        <div style={{ fontSize:15, fontWeight:800, color:C.text }}>{emp.name}</div>
                        <span style={{ fontSize:10, padding:'2px 8px', borderRadius:10, background:'#e0f2fe', color:'#0891b2', fontWeight:700 }}>👔 主管</span>
                        {isSelf && <span style={{ fontSize:10, padding:'2px 8px', borderRadius:10, background:C.greenBg, color:C.green, fontWeight:700 }}>我</span>}
                      </div>
                      {emp.title && !emp.title.includes('@') && <div style={{ fontSize:12, color:'#0891b2', fontWeight:600, marginTop:2 }}>{emp.title}</div>}
                      <div style={{ fontSize:11, color:C.muted, marginTop:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{emp.email}</div>
                    </div>
                  </div>
                  {/* 部門 / 電話 / 職位 */}
                  <div style={{ padding:'8px 16px', borderBottom:'1px solid #e0f2fe', display:'flex', gap:7, flexWrap:'wrap' }}>
                    {emp.department && <span style={{ fontSize:11, padding:'3px 10px', borderRadius:10, background:'#f0f9ff', color:'#0891b2' }}>🏢 {emp.department}</span>}
                    {emp.phone && <span style={{ fontSize:11, padding:'3px 10px', borderRadius:10, background:'#f8fafc', color:C.sub }}>📱 {emp.phone}</span>}
                    {pos && <span style={{ fontSize:11, padding:'3px 10px', borderRadius:10, background: pos.color+'15'||'#e0f2fe', color: pos.color||'#0891b2', fontWeight:700 }}>{pos.name}</span>}
                    {!emp.department && !emp.phone && !pos && <span style={{ fontSize:11, color:C.muted, fontStyle:'italic' }}>尚未填寫部門與電話</span>}
                  </div>
                  {/* 權限摘要 + 展開按鈕 */}
                  <div style={{ padding:'10px 16px', borderBottom:'1px solid #e0f2fe' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: isExpanded ? 6 : 0 }}>
                      <div style={{ fontSize:12, fontWeight:600, color:'#0891b2' }}>
                        📦 {enabledMods.length} 個模組
                        {enabledSpec.length > 0 && <span style={{ marginLeft:8, color:'#f97316' }}>🔑 {enabledSpec.length} 個特殊</span>}
                      </div>
                      <button style={{ fontSize:11, padding:'3px 10px', borderRadius:8, border:'1px solid #bae6fd', background:'#f0f9ff', color:'#0891b2', cursor:'pointer' }}
                        onClick={() => setExpandedPerms(isExpanded ? null : emp.id)}>
                        {isExpanded ? '收起 ▲' : '設定權限 ▼'}
                      </button>
                    </div>
                    {!isExpanded && enabledMods.length > 0 && (
                      <div style={{ display:'flex', flexWrap:'wrap', gap:4, marginTop:6 }}>
                        {enabledMods.slice(0,5).map(m => <span key={m.key} style={{ fontSize:10, padding:'2px 7px', borderRadius:6, background:'#e0f2fe', color:'#0891b2' }}>{m.icon} {m.label}</span>)}
                        {enabledMods.length > 5 && <span style={{ fontSize:10, padding:'2px 7px', borderRadius:6, background:'#f1f5f9', color:C.muted }}>+{enabledMods.length-5}</span>}
                      </div>
                    )}
                    {!isExpanded && enabledMods.length === 0 && <div style={{ fontSize:11, color:C.muted, fontStyle:'italic', marginTop:4 }}>尚未設定模組權限</div>}
                    {isExpanded && (
                      <div style={{ marginTop:10 }}>
                        {/* 快速套用職位權限 */}
                        {positions.length > 0 && (
                          <div style={{ display:'flex', gap:6, marginBottom:10, alignItems:'center', flexWrap:'wrap' }}>
                            <span style={{ fontSize:11, color:C.muted, fontWeight:600, flexShrink:0 }}>⚡ 快速套用：</span>
                            <select
                              value={quickApplyPos[emp.id] || ''}
                              onChange={e => setQuickApplyPos(p => ({ ...p, [emp.id]: e.target.value }))}
                              style={{ ...st.input, flex:1, minWidth:120, fontSize:12, padding:'4px 8px', height:30 }}>
                              <option value=''>選擇職位權限…</option>
                              {positions.map(p => (
                                <option key={p.id} value={p.id}>{p.icon || ''} {p.name}</option>
                              ))}
                            </select>
                            <button
                              disabled={!quickApplyPos[emp.id]}
                              onClick={() => {
                                const pid = quickApplyPos[emp.id]
                                if (pid) {
                                  handleApplyPositionDefaults(emp.id, pid)
                                  setQuickApplyPos(p => ({ ...p, [emp.id]: '' }))
                                }
                              }}
                              style={{ fontSize:11, padding:'4px 12px', borderRadius:8, border:`1px solid #0891b2`, background: quickApplyPos[emp.id] ? '#e0f2fe' : '#f1f5f9', color: quickApplyPos[emp.id] ? '#0891b2' : C.muted, cursor: quickApplyPos[emp.id] ? 'pointer' : 'not-allowed', fontWeight:700, flexShrink:0 }}>
                              套用
                            </button>
                          </div>
                        )}
                        <div style={{ fontSize:11, fontWeight:700, color:C.muted, marginBottom:6 }}>功能模組</div>
                        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(130px,1fr))', gap:5, marginBottom:10 }}>
                          {availableModules.map(m => {
                            const on = !!mods[m.key]
                            return (
                              <label key={m.key} style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 10px', borderRadius:8, cursor:'pointer', background: on?'#e0f2fe':'#f8fafc', border:`1.5px solid ${on?'#0891b2':C.border}`, fontSize:11 }}>
                                <input type="checkbox" checked={on} onChange={() => handleToggleModule(emp.id, m.key, on)} style={{ width:13, height:13, accentColor:'#0891b2' }} />
                                <span>{m.icon}</span>
                                <span style={{ fontWeight: on?700:400, color: on?'#0891b2':C.text }}>{m.label}</span>
                              </label>
                            )
                          })}
                        </div>
                        <div style={{ fontSize:11, fontWeight:700, color:'#f97316', marginBottom:6 }}>特殊權限</div>
                        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(130px,1fr))', gap:5 }}>
                          {SPECIAL_PERMS.map(s => {
                            const on = !!mods[s.key]
                            return (
                              <label key={s.key} style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 10px', borderRadius:8, cursor:'pointer', background: on?'#fff7ed':'#f8fafc', border:`1.5px solid ${on?'#f97316':C.border}`, fontSize:11 }}>
                                <input type="checkbox" checked={on} onChange={() => handleToggleModule(emp.id, s.key, on)} style={{ width:13, height:13 }} />
                                <span>{s.icon}</span>
                                <span style={{ fontWeight: on?700:400, color: on?'#f97316':C.text }}>{s.label}</span>
                              </label>
                            )
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                  {/* 動作按鈕 */}
                  {!isSelf && isAdmin && (
                    <div style={{ padding:'10px 14px', display:'flex', gap:7 }}>
                      <button style={{ ...st.btn('warning'), flex:1, padding:'8px 10px', fontSize:12 }} onClick={() => openEditManager(emp)}>✏️ 編輯</button>
                      <button style={{ ...st.btn('danger'), padding:'8px 12px', fontSize:12 }} onClick={() => handleDelete(emp.id, emp.name)}>🗑</button>
                    </div>
                  )}
                  {isSelf && <div style={{ padding:'10px 16px', fontSize:11, color:C.muted, fontStyle:'italic' }}>這是您自己的帳號</div>}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* ── 員工區塊 ── */}
      {employees.filter(e => e.role !== 'manager').length > 0 && (
        <div>
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12 }}>
            <div style={{ fontSize:14, fontWeight:800, color:C.sub }}>👤 員工列表</div>
            <div style={{ flex:1, height:2, background:`linear-gradient(to right, ${C.border}, transparent)`, borderRadius:2 }} />
          </div>
          {filtered.filter(e => e.role !== 'manager').length === 0 && search && <EmptyState icon="🔍" text="找不到符合條件的員工" />}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(310px,1fr))', gap:14 }}>
            {filtered.filter(e => e.role !== 'manager').map(emp => {
              const pos = positions.find(p => p.id === emp.position_id)
              const mods = modMap[emp.id] || {}
              const enabledMods = availableModules.filter(m => mods[m.key])
              const enabledSpec = SPECIAL_PERMS.filter(s => mods[s.key])
              const isSelf = emp.id === session?.user?.id
              const isExpanded = expandedPerms === emp.id
              const isPending = emp.approved === false
              return (
                <div key={emp.id} style={{ background:'#fff', borderRadius:16, border:`2px solid ${pos?.color ? pos.color+'44' : C.border}`, overflow:'hidden', boxShadow:'0 2px 8px rgba(0,0,0,0.05)', opacity: isPending?0.75:1 }}>
                  <div style={{ padding:'14px 16px', borderBottom:`1px solid ${C.border}`, display:'flex', gap:12, alignItems:'flex-start' }}>
                    <div style={{ width:44, height:44, borderRadius:'50%', background: pos?.color ? pos.color+'22':C.accentDim, display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, fontWeight:800, color: pos?.color||C.accent, flexShrink:0 }}>
                      {(emp.name||'?')[0].toUpperCase()}
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
                        <div style={{ fontSize:15, fontWeight:800, color:C.text }}>{emp.name}</div>
                        {emp.role==='admin' && <span style={{ fontSize:10, padding:'2px 8px', borderRadius:10, background:C.accentDim, color:C.accent, fontWeight:700 }}>⚙️ 管理員</span>}
                        {isPending && <span style={{ fontSize:10, padding:'2px 8px', borderRadius:10, background:C.yellowBg, color:C.yellow, fontWeight:700 }}>⏳ 待審核</span>}
                        {isSelf && <span style={{ fontSize:10, padding:'2px 8px', borderRadius:10, background:C.greenBg, color:C.green, fontWeight:700 }}>我</span>}
                      </div>
                      <div style={{ fontSize:11, color:C.muted, marginTop:2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{emp.email}</div>
                    </div>
                  </div>
                  <div style={{ padding:'10px 16px', borderBottom:`1px solid ${C.border}`, display:'flex', gap:7, flexWrap:'wrap' }}>
                    {emp.employee_id && <span style={{ fontSize:11, padding:'3px 10px', borderRadius:10, background:'#f8fafc', color:C.muted, fontFamily:'monospace' }}>🪪 {emp.employee_id}</span>}
                    {emp.department && <span style={{ fontSize:11, padding:'3px 10px', borderRadius:10, background:'#f8fafc', color:C.sub }}>🏢 {emp.department}</span>}
                    {emp.phone && <span style={{ fontSize:11, padding:'3px 10px', borderRadius:10, background:'#f8fafc', color:C.sub }}>📱 {emp.phone}</span>}
                    {pos && <span style={{ fontSize:11, padding:'3px 10px', borderRadius:10, background: pos.color+'15', color: pos.color, fontWeight:700 }}>{pos.name}</span>}
                    {!pos && <span style={{ fontSize:11, padding:'3px 10px', borderRadius:10, background:'#f1f5f9', color:C.muted }}>未指派職位</span>}
                  </div>
                  <div style={{ padding:'10px 16px', borderBottom:`1px solid ${C.border}` }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
                      <div style={{ fontSize:12, fontWeight:600, color:C.sub }}>
                        📦 {enabledMods.length} 個模組
                        {enabledSpec.length > 0 && <span style={{ marginLeft:8, color:'#f97316' }}>🔑 {enabledSpec.length} 個特殊</span>}
                      </div>
                      <button style={{ fontSize:11, padding:'3px 10px', borderRadius:8, border:`1px solid ${C.border}`, background:'#f8fafc', color:C.muted, cursor:'pointer' }}
                        onClick={() => setExpandedPerms(isExpanded ? null : emp.id)}>
                        {isExpanded ? '收起 ▲' : '展開 ▼'}
                      </button>
                    </div>
                    {!isExpanded && enabledMods.length > 0 && (
                      <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
                        {enabledMods.slice(0,6).map(m => <span key={m.key} style={{ fontSize:10, padding:'2px 7px', borderRadius:6, background:'#f1f5f9', color:C.sub }}>{m.icon} {m.label}</span>)}
                        {enabledMods.length > 6 && <span style={{ fontSize:10, padding:'2px 7px', borderRadius:6, background:'#f1f5f9', color:C.muted }}>+{enabledMods.length-6}</span>}
                      </div>
                    )}
                    {!isExpanded && enabledMods.length === 0 && <div style={{ fontSize:11, color:C.muted, fontStyle:'italic' }}>尚未設定模組權限</div>}
                    {isExpanded && (
                      <div style={{ marginTop:10 }}>
                        {/* 快速套用職位權限 */}
                        {positions.length > 0 && (
                          <div style={{ display:'flex', gap:6, marginBottom:10, alignItems:'center', flexWrap:'wrap' }}>
                            <span style={{ fontSize:11, color:C.muted, fontWeight:600, flexShrink:0 }}>⚡ 快速套用：</span>
                            <select
                              value={quickApplyPos[emp.id] || ''}
                              onChange={e => setQuickApplyPos(p => ({ ...p, [emp.id]: e.target.value }))}
                              style={{ ...st.input, flex:1, minWidth:120, fontSize:12, padding:'4px 8px', height:30 }}>
                              <option value=''>選擇職位權限…</option>
                              {positions.map(p => (
                                <option key={p.id} value={p.id}>{p.icon || ''} {p.name}</option>
                              ))}
                            </select>
                            <button
                              disabled={!quickApplyPos[emp.id]}
                              onClick={() => {
                                const pid = quickApplyPos[emp.id]
                                if (pid) {
                                  handleApplyPositionDefaults(emp.id, pid)
                                  setQuickApplyPos(p => ({ ...p, [emp.id]: '' }))
                                }
                              }}
                              style={{ fontSize:11, padding:'4px 12px', borderRadius:8, border:`1px solid ${C.accent}`, background: quickApplyPos[emp.id] ? C.accentDim : '#f1f5f9', color: quickApplyPos[emp.id] ? C.accent : C.muted, cursor: quickApplyPos[emp.id] ? 'pointer' : 'not-allowed', fontWeight:700, flexShrink:0 }}>
                              套用
                            </button>
                          </div>
                        )}
                        <div style={{ fontSize:11, fontWeight:700, color:C.muted, marginBottom:6 }}>功能模組</div>
                        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(130px,1fr))', gap:5, marginBottom:10 }}>
                          {availableModules.map(m => {
                            const on = !!mods[m.key]
                            return (
                              <label key={m.key} style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 10px', borderRadius:8, cursor:'pointer', background: on?C.accentDim:'#f8fafc', border:`1.5px solid ${on?C.accent:C.border}`, fontSize:11 }}>
                                <input type="checkbox" checked={on} onChange={() => handleToggleModule(emp.id, m.key, on)} style={{ width:13, height:13, accentColor:C.accent }} />
                                <span>{m.icon}</span>
                                <span style={{ fontWeight: on?700:400, color: on?C.accent:C.text }}>{m.label}</span>
                              </label>
                            )
                          })}
                        </div>
                        <div style={{ fontSize:11, fontWeight:700, color:'#f97316', marginBottom:6 }}>特殊權限</div>
                        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(130px,1fr))', gap:5 }}>
                          {SPECIAL_PERMS.map(s => {
                            const on = !!mods[s.key]
                            return (
                              <label key={s.key} style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 10px', borderRadius:8, cursor:'pointer', background: on?'#fff7ed':'#f8fafc', border:`1.5px solid ${on?'#f97316':C.border}`, fontSize:11 }}>
                                <input type="checkbox" checked={on} onChange={() => handleToggleModule(emp.id, s.key, on)} style={{ width:13, height:13 }} />
                                <span>{s.icon}</span>
                                <span style={{ fontWeight: on?700:400, color: on?'#f97316':C.text }}>{s.label}</span>
                              </label>
                            )
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                  {!isSelf && (
                    <div style={{ padding:'10px 14px', display:'flex', gap:7, flexWrap:'wrap' }}>
                      <button style={{ ...st.btn('warning'), flex:1, padding:'8px 10px', fontSize:12 }} onClick={() => openEdit(emp)}>✏️ 編輯</button>
                      {isAdmin && (
                        <button style={{ padding:'8px 12px', borderRadius:8, border:`1.5px solid ${emp.role==='admin'?C.redBorder:C.accent}`, background: emp.role==='admin'?C.redBg:C.accentDim, color: emp.role==='admin'?C.red:C.accent, fontSize:12, fontWeight:700, cursor:'pointer' }}
                          onClick={() => handleToggleAdmin(emp)}>
                          {emp.role==='admin' ? '⊘ 管理員' : '⚙ 設管理員'}
                        </button>
                      )}
                      <button style={{ ...st.btn('danger'), padding:'8px 12px', fontSize:12 }} onClick={() => handleDelete(emp.id, emp.name)}>🗑</button>
                    </div>
                  )}
                  {isSelf && <div style={{ padding:'10px 16px', fontSize:11, color:C.muted, fontStyle:'italic' }}>這是您自己的帳號</div>}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Manager Modal */}
      {showManagerModal && (
        <Modal title={editingManager ? `✏️ 編輯主管 — ${editingManager.name}` : '👔 新增主管帳號'} onClose={() => { setShowManagerModal(false); setEditingManager(null) }}>
          <div style={{ padding:'10px 14px', background:'#e0f2fe', borderRadius:8, marginBottom:16 }}>
            <div style={{ fontSize:12, fontWeight:700, color:'#0891b2' }}>主管帳號使用個人 Email 從企業入口登入，可瀏覽公司所有開通模組</div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
            <div>
              <label style={st.label}>姓名 *</label>
              <input style={st.input} value={managerForm.name} placeholder="王大明" onChange={e => setManagerForm(f=>({...f,name:e.target.value}))} autoFocus />
            </div>
            <div>
              <label style={st.label}>職稱</label>
              <input style={st.input} value={managerForm.title} placeholder="例：總經理、執行長" onChange={e => setManagerForm(f=>({...f,title:e.target.value}))} />
            </div>
            {!editingManager && (
              <div style={{ gridColumn:'span 2' }}>
                <label style={st.label}>Email *（登入帳號）</label>
                <input style={st.input} type="email" value={managerForm.email} placeholder="manager@gmail.com" onChange={e => setManagerForm(f=>({...f,email:e.target.value}))} />
                <div style={{ fontSize:11, color:C.muted, marginTop:4 }}>從「企業登入」使用此 Email 登入系統</div>
              </div>
            )}
            <div style={{ gridColumn: editingManager ? 'span 2' : '1' }}>
              <label style={st.label}>{editingManager ? '重設密碼' : '初始密碼 *'}（至少 6 位）</label>
              <input style={st.input} type="password" value={managerForm.password} placeholder={editingManager ? '不填則維持原密碼' : '設定初始密碼'} onChange={e => setManagerForm(f=>({...f,password:e.target.value}))} />
            </div>
            <div>
              <label style={st.label}>部門</label>
              <select style={st.input} value={managerForm.department} onChange={e => setManagerForm(f=>({...f,department:e.target.value}))}>
                <option value="">-- 不指定 --</option>
                {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label style={st.label}>電話</label>
              <input style={st.input} value={managerForm.phone} placeholder="0912-345-678" onChange={e => setManagerForm(f=>({...f,phone:e.target.value}))} />
            </div>
            <div style={{ gridColumn:'span 2' }}>
              <label style={st.label}>職位 <span style={{ color:C.muted, fontWeight:400 }}>(決定模組權限範圍)</span></label>
              {positions.length > 0 ? (
                <select style={st.input} value={managerForm.position_id} onChange={e => setManagerForm(f=>({...f,position_id:e.target.value}))}>
                  <option value="">-- 不指定（取得全部公司模組）--</option>
                  {positions.map(p => <option key={p.id} value={p.id}>{p.name}{p.name_en ? ` · ${p.name_en}` : ''}{p.department ? ` [${p.department}]` : ''}</option>)}
                </select>
              ) : (
                <div style={{ padding:'10px 14px', background:C.yellowBg, borderRadius:10, fontSize:12, color:C.yellow }}>
                  ⚠️ 請先到「職位管理」建立職位，才能指派職位給主管
                </div>
              )}
              {managerForm.position_id
                ? <div style={{ fontSize:11, color:'#0891b2', marginTop:4 }}>ℹ️ 主管將依職位設定取得特定模組權限，而非所有公司模組</div>
                : <div style={{ fontSize:11, color:C.muted, marginTop:4 }}>未指定職位時，主管可存取公司所有已開通模組</div>
              }
            </div>
          </div>
          <div style={{ display:'flex', gap:10, marginTop:20 }}>
            <button style={{ ...st.btn(), flex:1, padding:'14px', background:'#0891b2', borderColor:'#0891b2' }} onClick={handleSaveManager} disabled={savingManager}>
              {savingManager ? '儲存中...' : editingManager ? '✓ 確認更新' : '✓ 建立主管帳號'}
            </button>
            <button style={{ ...st.btn('default'), padding:'14px 20px' }} onClick={() => { setShowManagerModal(false); setEditingManager(null) }}>取消</button>
          </div>
        </Modal>
      )}

      {/* Add / Edit Modal */}
      {showModal && (
        <Modal title={editingUser ? `✏️ 編輯員工 — ${editingUser.name}` : '+ 新增員工'} onClose={() => setShowModal(false)}>

          {/* 必填區塊 */}
          <div style={{ padding:'10px 14px', background:C.accentDim, borderRadius:8, marginBottom:14 }}>
            <div style={{ fontSize:12, fontWeight:700, color:C.accent }}>基本資料（必填）</div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14 }}>
            <div>
              <label style={st.label}>姓名 *</label>
              <input style={st.input} value={form.name} placeholder="王小明" onChange={e => setForm(f=>({...f,name:e.target.value}))} autoFocus />
            </div>
            <div>
              <label style={st.label}>工號 * <span style={{ color:C.muted, fontWeight:400 }}>(登入用)</span></label>
              <input style={st.input} value={form.employee_id} placeholder="A001"
                onChange={e => setForm(f=>({...f,employee_id:e.target.value}))} />
              {!editingUser && (
                <div style={{ fontSize:11, color:C.muted, marginTop:4 }}>員工用此工號登入系統</div>
              )}
              {editingUser && form.employee_id.trim().toLowerCase() !== (editingUser.employee_id||'').toLowerCase() && (
                <div style={{ fontSize:11, color:'#d97706', marginTop:4, padding:'6px 10px', background:'#fef9c3', borderRadius:8, border:'1px solid #fcd34d' }}>
                  ⚠️ 工號改變後，員工須使用新工號 <strong>{form.employee_id.trim().toUpperCase()}</strong> 登入
                </div>
              )}
              {editingUser && form.employee_id.trim().toLowerCase() === (editingUser.employee_id||'').toLowerCase() && (
                <div style={{ fontSize:11, color:C.muted, marginTop:4 }}>更改工號會同步更新登入帳號</div>
              )}
            </div>
            <div style={{ gridColumn: 'span 2' }}>
              <label style={st.label}>
                {editingUser ? '重設密碼' : '密碼 *'}
                <span style={{ color:C.muted, fontWeight:400 }}> （純數字，至少 6 位）</span>
              </label>
              <input style={{ ...st.input, letterSpacing:4, fontSize:18 }} type="password" inputMode="numeric"
                value={form.password} placeholder={editingUser ? '不填則維持原密碼' : '例：123456'}
                maxLength={12}
                onChange={e => setForm(f=>({...f,password:e.target.value.replace(/\D/g,'')}))} />
            </div>
          </div>

          {/* 選填區塊 */}
          <div style={{ padding:'10px 14px', background:'#f8fafc', borderRadius:8, marginBottom:14 }}>
            <div style={{ fontSize:12, fontWeight:700, color:C.muted }}>其他資料（選填）</div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
            <div>
              <label style={st.label}>部門</label>
              <select style={st.input} value={form.department} onChange={e => setForm(f=>({...f,department:e.target.value}))}>
                <option value="">-- 選擇部門 --</option>
                {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label style={st.label}>電話</label>
              <input style={st.input} value={form.phone} placeholder="0912-345-678" onChange={e => setForm(f=>({...f,phone:e.target.value}))} />
            </div>
            <div style={{ gridColumn:'span 2' }}>
              <label style={st.label}>職位 <span style={{ color:C.muted, fontWeight:400 }}>(可套用預設模組權限)</span></label>
              {positions.length > 0 ? (
                <select style={st.input} value={form.position_id} onChange={e => setForm(f=>({...f,position_id:e.target.value}))}>
                  <option value="">-- 選擇職位 --</option>
                  {positions.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              ) : (
                <div style={{ padding:'10px 14px', background:C.yellowBg, borderRadius:10, fontSize:12, color:C.yellow }}>
                  ⚠️ 請先到「職位管理」建立職位
                </div>
              )}
            </div>
          </div>

          <div style={{ display:'flex', gap:10, marginTop:20 }}>
            <button style={{ ...st.btn(), flex:1, padding:'14px' }} onClick={handleSave} disabled={saving}>
              <span>{saving ? '儲存中...' : editingUser ? '確認更新' : '✓ 建立員工帳號'}</span>
            </button>
            <button style={{ ...st.btn('default'), padding:'14px 20px' }} onClick={() => setShowModal(false)}>取消</button>
          </div>
        </Modal>
      )}
    </div>
  )
}
